import { useState, useEffect } from 'react';
import { Grid, Modal, CircularProgress, FormHelperText, Autocomplete } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import showToast from 'components/Toast/Toast';
import { editWeekPost } from 'utils/requests/communities';
import * as S from '../forms/style';
import { useFormik } from 'formik';
import * as Yup from "yup";

const formatOptions = [
  { value: 'video', label: 'Vídeo' },
  { value: 'reel', label: 'Reel' },
  { value: 'story', label: 'Story' },
  { value: 'post', label: 'Post' },
  { value: 'carousel', label: 'Carrossel' }
];

const objectiveOptions = [
  { value: 'engage', label: 'Engajar meu público' },
  { value: 'promote', label: 'Divulgar um produto ou curso' },
  { value: 'inspire', label: 'Inspirar com algo pessoal' },
  { value: 'response', label: 'Gerar comentário ou resposta' },
  { value: 'other', label: 'Outro' }
];

const EditScriptForm = ({ openModal, closeModal, selectedScript, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [validateFields, setValidateFields] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      content: selectedScript?.content || '',
      format: selectedScript?.format || '',
      objective: selectedScript?.objective || '',
      observation: selectedScript?.observation || ''
    },

    validateOnChange: validateFields,
    validateOnBlur: false,

    validationSchema: Yup.object({
      content: Yup.string().required('O conteúdo do roteiro é obrigatório'),
      format: Yup.string().required('O formato é obrigatório'),
      objective: Yup.string().required('O objetivo é obrigatório'),
      observation: Yup.string()
    }),
    
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (!selectedScript) {
          showToast("Script não encontrado", "error");
          return;
        }

        const updatedScript = {
          content: values.content,
          format: values.format,
          objective: values.objective,
          observation: values.observation,
          createdAt: selectedScript.createdAt,
        };

        const updatedScriptData = {
          tone: selectedScript.originalScriptData?.tone || '',
          title: selectedScript.originalScriptData?.title || selectedScript.title || '',
          format: selectedScript.originalScriptData?.format || '',
          objective: selectedScript.originalScriptData?.objective || '',
          description: selectedScript.originalScriptData?.description || '',
          generated_script: updatedScript
        };

        const updateResponse = await editWeekPost(selectedScript.id, {
          script_data: JSON.stringify(updatedScriptData)
        });

        if (updateResponse) {
          showToast("Roteiro editado com sucesso!", "success");
          closeModal();
          refetch();
        } else {
          showToast("Erro ao salvar edições do roteiro", "error");
        }
      } catch (error) {
        console.error("Erro ao editar roteiro:", error);
        showToast("Erro ao editar roteiro", "error");
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (openModal) {
      setValidateFields(false);
    }
  }, [openModal]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value);
    setValidateFields(true);
  };

  return (
    <Modal
      open={openModal}
      onClose={closeModal}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <S.ModalContainer>
        <S.CloseButton
          aria-label="close"
          onClick={closeModal}
        >
          <CloseIcon />
        </S.CloseButton>
        <S.ModalTitle>
          <S.ModalTitleText variant="h5">
            Editar Roteiro
          </S.ModalTitleText>
        </S.ModalTitle>
        <S.ModalContent>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              Conteúdo do Roteiro
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12} md={12} xl={12}>
              <S.Input
                hiddenLabel
                name='content'
                type='text'
                placeholder="Digite o conteúdo do roteiro..."
                fullWidth
                multiline
                rows={8}
                value={formik.values.content}
                onChange={handleChange}
              />
            </S.InputGridBuildScript>
            {formik.touched.content && formik.errors.content && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.content}
              </FormHelperText>
            )}
          </S.FormSection>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              Formato
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12} md={12} xl={12}>
              <Autocomplete
                limitTags={10}
                disablePortal
                name="format"
                value={formatOptions.find(option => option.value === formik.values.format) || null}
                onChange={(e, value) => {
                  handleChange({
                    target: {
                      name: 'format',
                      value: value ? value.value : ''
                    }
                  });
                }}
                options={formatOptions}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => option.label}
                renderInput={(params) =>
                  <S.InputBuildScript {...params} 
                  placeholder='Selecione o formato' />
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </S.InputGridBuildScript>
            {formik.touched.format && formik.errors.format && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.format}
              </FormHelperText>
            )}
          </S.FormSection>
          <S.FormSection>
            <S.SectionTitle variant="h6">
              Objetivo
            </S.SectionTitle>
            <S.InputGridBuildScript item xs={12} md={12} xl={12}>
              <Autocomplete
                limitTags={10}
                disablePortal
                name="objective"
                value={objectiveOptions.find(option => option.value === formik.values.objective) || null}
                onChange={(e, value) => {
                  handleChange({
                    target: {
                      name: 'objective',
                      value: value ? value.value : ''
                    }
                  });
                }}
                options={objectiveOptions}
                sx={{ width: "100%" }}
                getOptionLabel={(option) => option.label}
                renderInput={(params) =>
                  <S.InputBuildScript {...params} 
                  placeholder='Selecione o objetivo' />
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </S.InputGridBuildScript>
            {formik.touched.objective && formik.errors.objective && (
              <FormHelperText error sx={{ color: '#f44336', mt: 1 }}>
                {formik.errors.objective}
              </FormHelperText>
            )}
          </S.FormSection>
          <Grid container sx={{ marginTop: '20px' }}>
            <S.SectionTitle variant="h6">
              Observações
            </S.SectionTitle>
            <S.InputGridBuildScript item md={12} xl={12}>
              <S.Input
                hiddenLabel
                name='observation'
                type='text'
                placeholder="Adicione observações (opcional)"
                fullWidth
                multiline
                rows={3}
                value={formik.values.observation}
                onChange={handleChange}
              />
            </S.InputGridBuildScript>
          </Grid>
        </S.ModalContent>
        <S.ButtonContainer>
          <S.EditButton
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={loading}
          >
            {loading && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </S.EditButton>
        </S.ButtonContainer>
      </S.ModalContainer>
    </Modal>
  );
};

export default EditScriptForm;